import {IValidationItem} from "../../definitions/utils/validator/validator/IValidationItem";
import {ElementValidateParams} from "../../definitions/utils/validator/elementValidatorUtil/ElementValidateParams";
import {DOM} from "../DOM";

/**
 * Contains helper functions for element validations
 * - Select element
 * - Input/textarea element
 */
export class ElementValidatorUtil {

    /**
     * Generate validation parameters as internal struct for validator
     * @param element - input element with validations
     * @private
     */
    public static getValidationParams(element: HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement) : ElementValidateParams {

        const rules = element.attributes.getNamedItem("data-nette-rules");
        const rulesDecoded = rules !== null ? JSON.parse(rules.value) as IValidationItem[] : [];
        const params : ElementValidateParams = {regexes: []};

        rulesDecoded.forEach((validation: IValidationItem) => {

            const validationMsg = validation.msg ? validation.msg : null;
            if (validation.op && validation.op === ":pattern" && validation.arg) {
                params.regexes.push({regex: new RegExp(validation.arg), problem: validationMsg});
            } else if (validation.op && validation.op === ":minLength" && validation.arg) {
                params.minLength = {length: parseInt(validation.arg), problem: validationMsg};
            } else if (validation.op && validation.op === ":customMaxLength" && validation.arg) {
                params.customMaxLength = {length: parseInt(validation.arg), problem: validationMsg};
            } else if (validation.op && validation.op === ":oneOff" && validation.arg) {
                params.filledOneOf = {inputNames: validation.arg, problem: validationMsg};
            } else if (validation.op && validation.op === ":filled") {
                params.filled = {filled: true, problem: validationMsg};
            } else if (validation.op && validation.op === ":email") {
                params.email = {email:true, problem:validationMsg};
            } else if (validation.op && validation.op === ":equal" && validation.arg && validation.arg.control) {
                params.equalControlName = {name: validation.arg.control, problem: validationMsg};
            } else if (validation.op && validation.op === ":fileSize" && validation.arg) {
                params.maxFileSize = {maxSize: parseInt(validation.arg), problem: validationMsg};
            } else if (validation.op && validation.op === ":max" && validation.arg) {
                params.maxNumber = {max: parseInt(validation.arg), problem: validationMsg};
            } else if (validation.op && validation.op === ":min" && validation.arg) {
                params.minNumber = {min: parseInt(validation.arg), problem: validationMsg};
            } else if (validation.op && validation.op === ":dnsValidation" && validation.arg && validation.arg.PATTERNS && validation.arg.TYPE_INPUT_NAME) {
                params.dnsValidation = {patterns: validation.arg.PATTERNS, typeInputName: validation.arg.TYPE_INPUT_NAME, problem: validationMsg};
            } else if (validation.op && validation.op === ":stringValidation" && validation.arg) {
                params.stringValidation = {patternType: validation.arg, problem: validationMsg};
            } else if (validation.op && validation.op === ":revalidate" && validation.arg) {
                params.revalidate = {nameList: validation.arg};
            } else if (validation.op && validation.op === ":string1CnameASameValidation" && validation.arg) {
                params.string1CnameASameValidation = {typeInputName: validation.arg.TYPE_INPUT_NAME, problem: validationMsg};
            } else if (validation.op && validation.op === ":date" && validation.arg) {
                params.date = {onlyFuture: validation.arg.onlyFuture, problem: validationMsg};
            }
        });
        return params;
    }

    /**
     * Update help element on input
     * Try to find input (todo recursion)
     * @param element - input element
     * @param text - error message text
     * @param valid - is now valid
     */
    public static updateHelpElement(element: HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement, text: string|null, valid: boolean) : void {

        // Error message is not defined
        if (text === null) { return; }

        // Check if possible
        let parentElement = element.parentElement as HTMLElement|null;
        if (!parentElement || !parentElement.classList.contains('customDesign-forms-field')) { parentElement = parentElement?.parentElement as HTMLElement|null; }
        if (!parentElement || !parentElement.classList.contains('customDesign-forms-field')) { parentElement = parentElement?.parentElement as HTMLElement|null; }
        if (!parentElement || !parentElement.classList.contains('customDesign-forms-field')) { return; }

        // Get already created
        const foundAlreadyCreatedElements = parentElement.getElementsByClassName('inputInvalidHelp');

        // Without text - don't show
        if (foundAlreadyCreatedElements.length > 0 && (element.dataset.filled === 'no' || element.dataset.valid === 'true')) {
            for (let i = 0; i < foundAlreadyCreatedElements.length; i++) {
                parentElement.removeChild(foundAlreadyCreatedElements[i]);
            }
            return;
        } else if (element.dataset.filled === 'no' || element.dataset.valid === 'true' || valid) {
            return;
        }

        // Already created just update text
        if (foundAlreadyCreatedElements.length > 0) {
            const alreadyCreatedElement = foundAlreadyCreatedElements[0] as HTMLElement;
            const textElement = DOM.Parser.getHtmlElementByClass('text', alreadyCreatedElement);
            textElement.innerText = text;
            return;
        }

        // Create text
        const textElement = document.createElement('div');
        textElement.classList.add('inputInvalidHelp-container-text', 'text');
        textElement.innerText = text;

        // Create arrow inside
        const arrowInsideElement = document.createElement('div');
        arrowInsideElement.classList.add('inputInvalidHelp-container-arrow-inside');

        // Create arrow
        const arrowElement = document.createElement('div');
        arrowElement.classList.add('inputInvalidHelp-container-arrow', 'arrow');
        arrowElement.append(arrowInsideElement);

        // Create container
        const containerElement = document.createElement('div');
        containerElement.classList.add('inputInvalidHelp-container');

        // Create element
        const finalElement = document.createElement('div');
        finalElement.classList.add('inputInvalidHelp');

        // Build node
        parentElement.appendChild(finalElement).appendChild(containerElement).append(textElement, arrowElement);
    }

    /**
     * Initialize trigger to show hidden password
     * @param element - password input element
     * @private
     */
    public static initializeShowPasswordButton(element: HTMLInputElement) {

        if (element.type !== 'password' || element.parentElement === null) { return; }
        const toggleElements = element.parentElement.getElementsByClassName('input-field-password-show');
        if (toggleElements.length === 0) { return; }
        const trigger = toggleElements[0] as HTMLElement;

        trigger.addEventListener('click', () => {
            const isHidden = DOM.Parser.getDataByNameFromElement('toggled', trigger) === 'password';
            trigger.dataset.toggled = isHidden ? 'text' : 'password';
            element.setAttribute('type', isHidden ? 'text' : 'password');
            DOM.Parser.getHtmlElementByQuery(`[data-toggle='${!isHidden ? 'password' : 'text'}']`, trigger).style.display = 'block';
            DOM.Parser.getHtmlElementByQuery(`[data-toggle='${isHidden ? 'password' : 'text'}']`, trigger).style.display = 'none';
        });
    }

    /**
     * Validate number input
     * Check if number is typed
     * @param element - input element
     * @private
     */
    public static triggerElementValidateNumberOnKey(element: HTMLInputElement) {

        element.addEventListener("keydown", (event: KeyboardEvent) => {
            if (Number.isNaN(event.key)) {
                event.preventDefault();
                event.stopPropagation();
                return false;
            } return true;
        });
    }

    public static setElementCanDisplayErrors(element: HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement): void {

        const parentElement = element.closest('.customDesign-forms-field') as HTMLElement;
        if (parentElement === null)  return;
        parentElement.classList.add('customDesign-forms-field-displayErrors');
    }
}


